import { submitFormJson } from 'widgets/toolbox/ajax';
import { get } from 'widgets/toolbox/util';

/**
 * @typedef {ReturnType<typeof import('widgets/checkout/AddressCheckoutForm').default>} AddressCheckoutForm
 * @typedef {InstanceType<ReturnType<typeof import('widgets/forms/InputRadio').default>>} inputRadio
 * @typedef {InstanceType<ReturnType<typeof import('widgets/forms/BasicInput').default>>} basicInput
 * @typedef {InstanceType<ReturnType<typeof import('widgets/forms/InputTextarea').default>>} inputTextarea
 * @typedef {InstanceType<ReturnType<typeof import('widgets/forms/InputCheckbox').default>>} inputCheckbox
 * @typedef {InstanceType<ReturnType<typeof import('widgets/checkout/RadioSelector').default>>} radioSelector
 */

/**
 * @param {AddressCheckoutForm} AddressCheckoutForm Base widget for extending
 * @returns {typeof ShippingForm} Shipping Form widget
 */
export default function (AddressCheckoutForm) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class ShippingForm
     * @augments AddressCheckoutForm
     * @property {string} data-select-shipping-method-url
     * @property {string} data-update-shipping-list-url
     * @classdesc Represents checkout shipping form with next features:
     * 1. Updates shipping methods list, when some of the dependencies have been changed
     * 2. Handles form submission server errors
     * 3. Toggles gift functionality
     * 4. Handles shipping method selection
     *
     * Widget has next relationship:
     * * Updates shipping methods list be calling {@link RadioSelector#updateState}
     * * Toggles gift message input by calling {@link InputTextarea#toggle}
     * * Listens event {@link InputRadio#change} from shipping methods list to handle shipping method change
     * * Listens event {@link InputCheckbox#change} from gift checkbox to toggle gift message input
     * @example <caption>Checkout shipping form</caption>
     * <form
     *     autocomplete="on"
     *     novalidate
     *     data-widget="shippingForm"
     *     data-event-submit.prevent="handleSubmit"
     *     data-widget-event-updateshippingform="updateSummaryEv"
     *     data-address-selector="shippingAddressSelector"
     *     data-address-fields="${[
     *         shippingFields.firstName.htmlName,
     *         shippingFields.lastName.htmlName,
     *         shippingFields.address1.htmlName,
     *         shippingFields.address2.htmlName,
     *         shippingFields.country.htmlName,
     *         shippingFields.states.stateCode.htmlName,
     *         shippingFields.city.htmlName,
     *         shippingFields.postalCode.htmlName,
     *         shippingFields.phone.htmlName
     *     ].join(',')}"
     *     data-widget-event-submit="shippingSubmitted"
     *     data-select-shipping-method-url="${URLUtils.https('CheckoutShippingServices-SelectShippingMethod')}"
     *     data-update-shipping-list-url="${URLUtils.https('CheckoutShippingServices-UpdateShippingMethodsList')}"
     *     action="${URLUtils.url('CheckoutShippingServices-SubmitShipping')}"
     *     data-form-definition-url="${URLUtils.url('Forms-FormDefinition', 'form', 'shipping')}"
     *     <isprint value=${pdict.forms.shippingForm.attributes} encoding="off"/>
     *     data-address-autocomplete-enabled="${!!require('util/sitepref').getValue('googleAutocompleteEnabled')}"
     * >
     *     ...
     *         <div
     *             class="b-form-message"
     *             role="alert"
     *             hidden="hidden"
     *             data-ref="errorMessageLabel"
     *         ></div>
     *     ...
     *     <isinclude template="checkout/shipping/shipmentSelector" />
     *     ...
     *     <isinclude template="checkout/shipping/shippingAddress" />
     *     ...
     *     <isinclude template="checkout/shipping/shippingMethod" />
     *     ...
     *     <isinclude template="checkout/shipping/isGift" />
     *     ...
     * </form>
     */
    class ShippingForm extends AddressCheckoutForm {
        prefs() {
            return {
                updateShippingListUrl: '',
                selectShippingMethodUrl: '',
                ...super.prefs()
            };
        }

        /**
         * @description Update list of shipping methods after updating form fields from the server
         * @returns {void}
         */
        formDataUpdated() {
            this.updateShippingState();
        }

        /**
         * @description Show errors after shipping form submission or set selected address
         * @param {object} data - Server response object
         * @param {boolean} [data.error] - Is some submission errors
         * @param {string[]} [data.serverErrors] - Common shipping step error
         * @param {object[]} [data.fieldErrors] - Shipping form fields errors
         * @returns {void}
         */
        onSubmitted(data) {
            if (data.error && data.serverErrors && data.serverErrors.length) {
                this.setError(data.serverErrors.join(''), true);
            } else if (data.error && data.fieldErrors) {
                this.showAddressForm();
                data.fieldErrors.forEach((entries) => {
                    let firstError = true;
                    Object.entries(entries).forEach(([name, errorMsg]) => {
                        this.getById(name, (/** @type {basicInput} */address) => {
                            if (address.id === name) {
                                if (firstError) {
                                    address.setFocus();
                                }
                                firstError = false;
                                address.setError(errorMsg);
                            }
                        });
                    });
                });
            } else {
                const shippingAddressID = get(data, 'order.shipping.0.shippingAddress.addressUUID');
                this.customer.addresses = [];
                if (data && data.customer) {
                    for (let i = 0; i < data.customer.addresses.length; i++) {
                        if (data.customer.addresses[i].ID !== 'BOPIS') {
                            this.customer.addresses.push(data.customer.addresses[i]);
                        }
                    }
                }
                // this.customer.addresses = data.customer.addresses;
                this.setSelectedAddress(shippingAddressID);
                super.onSubmitted(data);
            }
        }

        /**
         * @description Update shipping methods list
         * @emits ShippingForm#updateshippingform
         * @returns {void}
         */
        updateShippingState() {
            this.showProgressBar();
            submitFormJson(
                this.prefs().updateShippingListUrl,
                this.getFormFieldsShort()
            ).then((response) => {
                if (response && response.order) {
                    this.updateState(response.order);
                    /**
                     * @description Event to update summary block
                     * @event ShippingForm#updateshippingform
                     */
                    this.emit('updateshippingform', response.order);
                }
            }).finally(() => {
                this.hideProgressBar();
            });
        }

        /**
         * @description Rerender shipping methods list
         * @param {object} [basket] - Basket object
         * @returns {void}
         */
        updateState(basket) {
            this.getById('shippingMethodList', (/** @type {radioSelector} */shippingMethodList) => {
                return shippingMethodList.updateState(basket);
            });
        }

        /**
         * @description Select shipping method handler
         * @param {inputRadio} inputRadio - Shipping method`s input
         * @listens InputRadio#change
         * @emits ShippingForm#updateshippingform
         * @emits "cart.update.shipping.method"
         * @returns {void}
         */
        selectShippingMethod(inputRadio) {
            this.showProgressBar();
            submitFormJson(
                this.prefs().selectShippingMethodUrl,
                { methodID: inputRadio.getDataValue(), ...this.getFormFieldsShort() }
            ).then((response) => {
                if (response && response.order) {
                    this.updateOrder(response.order);
                    /**
                     * @description Event to update summary block
                     * @event ShippingForm#updateshippingform
                     */
                    this.emit('updateshippingform', response.order);
                    this.eventBus().emit(
                        'cart.update.shipping.method',
                        response.order.shipping[0].selectedShippingMethod.displayName
                    );
                }
            }).finally(() => {
                this.hideProgressBar();
            });
        }

        /**
         * @description Toggle gift message input
         * @param {inputCheckbox} inputCheckbox - Is gift checkbox
         * @listens InputCheckbox#change
         * @returns {void}
         */
        toggleGiftWrap(inputCheckbox) {
            const checked = inputCheckbox.getValue();

            this.getById('giftMessage', (/** @type {inputTextarea} */giftMessage) => {
                giftMessage.toggle(Boolean(checked));
                if (checked) {
                    giftMessage.ref('field').attr('required', true);
                } else {
                    giftMessage.ref('field').attr('required', false);
                }
            });
        }

        /**
         * @description Return applicable addresses for current form
         * @param {object} order - order data
         * @param {object} customer - customer data
         * @returns {Array} returns shipping addresses
         */
        getAddressOptions(order, customer) {
            const shippingAddress = get(order, 'shipping.0.shippingAddress');
            const customerAddresses = customer.addresses;
            const allAddresses = [...customerAddresses];

            if (shippingAddress) {
                allAddresses.unshift(shippingAddress);
            }

            return allAddresses;
        }
    }

    return ShippingForm;
}
